import { useRef, useState, useEffect } from 'react';
import { IconSparkles, IconUpload, IconFileText, IconX, IconChevronDown, IconLock, IconEye, IconGift } from '@tabler/icons-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import customerService from '../../../services/sohozJobCustomerService';
import usageService from '../../../services/usageService';
import { useAuthStore } from '../../../store/authStore';

const MAX_FILES = 6;
const MAX_SIZE = 15 * 1024 * 1024;

function isAllowed(f) {
    const n = f.name.toLowerCase();
    return f.type === 'application/pdf' || f.type.startsWith('image/') || f.type.includes('word') || n.endsWith('.docx');
}

// One uploaded file row — image files show a thumbnail that opens a full preview.
function FileRow({ file, onRemove, onPreview, previewLabel }) {
    const [url, setUrl] = useState('');
    const isImage = file.type.startsWith('image/');
    useEffect(() => {
        if (!isImage) return undefined;
        const u = URL.createObjectURL(file);
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [file, isImage]);

    const canPreview = isImage && url;

    return (
        <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
            {canPreview ? (
                <button
                    type="button"
                    onClick={() => onPreview(url, file.name)}
                    title={previewLabel}
                    className="relative w-10 h-10 rounded overflow-hidden flex-shrink-0 border border-zinc-200 dark:border-zinc-700 group"
                >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-colors">
                        <IconEye size={15} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                </button>
            ) : (
                <span className="w-10 h-10 rounded bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center flex-shrink-0">
                    <IconFileText size={18} className="text-zinc-400" />
                </span>
            )}
            <span className="flex-1 min-w-0 truncate text-[13px] text-zinc-700 dark:text-zinc-200">{file.name}</span>
            {canPreview && (
                <button type="button" onClick={() => onPreview(url, file.name)} title={previewLabel} className="text-zinc-400 hover:text-primary flex-shrink-0">
                    <IconEye size={16} />
                </button>
            )}
            <button type="button" onClick={onRemove} title="" className="text-zinc-400 hover:text-red-500 flex-shrink-0"><IconX size={15} /></button>
        </div>
    );
}

/**
 * Scan documents (PDF / image / DOCX) or pasted text → AI fills the form.
 * Files are sent for scanning only and are NOT stored.
 */
// Convert ASCII digits to Bengali numerals in bn locale (matches how credits are
// shown elsewhere, e.g. the avatar credit chip in UserMenu).
const toLocalDigits = (n, isBn) => (isBn
    ? String(n ?? 0).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d])
    : String(n ?? 0));

export default function CvImportModal({ open, onClose, onApply }) {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const { user, getCurrentUser } = useAuthStore();
    const credit = user?.credit ?? 0;
    // Per-analysis cost for FORM_FILL, read live from Settings (the active-unit rate,
    // i.e. walletCreditCost_FORM_FILL for wallet users). null = still loading.
    const [formFillCost, setFormFillCost] = useState(null);
    const isFree = formFillCost === 0;
    // Only block on empty balance when the action actually costs something.
    const noCredit = !isFree && credit <= 0;
    const [files, setFiles] = useState([]);
    const [showText, setShowText] = useState(false);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [preview, setPreview] = useState(null); // { url, name } of an image being previewed
    const fileRef = useRef(null);

    // Load the live FORM_FILL per-analysis cost each time the modal opens.
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        usageService.getCreditCosts()
            .then((res) => {
                const costs = res?.data ?? res;
                const c = Number(costs?.FORM_FILL);
                if (alive && Number.isFinite(c)) setFormFillCost(c);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [open]);

    const addFiles = (list) => {
        const incoming = Array.from(list || []);
        setFiles((prev) => {
            const merged = [...prev];
            for (const f of incoming) {
                if (merged.length >= MAX_FILES) { notifications.show({ message: t('sohozJob.cv.maxFiles', { n: MAX_FILES }), color: 'orange' }); break; }
                if (!isAllowed(f)) { notifications.show({ message: t('sohozJob.cv.badType'), color: 'red' }); continue; }
                if (f.size > MAX_SIZE) { notifications.show({ message: t('sohozJob.cv.tooBig'), color: 'red' }); continue; }
                if (merged.some((m) => m.name === f.name && m.size === f.size)) continue;
                merged.push(f);
            }
            return merged;
        });
    };

    const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const handleAnalyze = async () => {
        if (!files.length && !text.trim()) {
            notifications.show({ title: t('common.error'), message: t('sohozJob.cv.empty'), color: 'red' });
            return;
        }
        if (noCredit) {
            notifications.show({ message: t('sohozJob.cv.noCredit'), color: 'red' });
            return;
        }
        setLoading(true);
        try {
            const fields = files.length
                ? await customerService.parseCvFiles(files)
                : await customerService.parseCv(text.trim());
            const count = Object.keys(fields || {}).length;
            if (!count) {
                notifications.show({ message: t('sohozJob.aiNoData'), color: 'orange' });
                return;
            }
            // Analysis succeeded (HTTP 200): the backend already charged one credit
            // for this fill-up — refresh the balance so the button disables at zero.
            onApply(fields);
            getCurrentUser().catch(() => {});
            notifications.show({ title: t('common.success'), message: t('sohozJob.aiFilled'), color: 'green' });
            onClose();
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('sohozJob.aiFailed'), color: 'red' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onClose(); }}>
            <DialogContent className="max-w-xl font-hind">
                <DialogHeader className="mb-3">
                    <DialogTitle className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
                            <IconSparkles size={18} />
                        </span>
                        {t('sohozJob.aiModalTitle')}
                    </DialogTitle>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('sohozJob.cv.desc')}</p>
                </DialogHeader>

                <div className="space-y-3">
                    {/* Dropzone */}
                    <div
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                            dragging ? 'border-primary bg-primary/5' : 'border-zinc-200 dark:border-zinc-700 hover:border-primary/40 hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
                        }`}
                    >
                        <span className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800">
                            <IconUpload size={22} className="text-zinc-400 dark:text-zinc-500" />
                        </span>
                        <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('sohozJob.cv.dropTitle')}</p>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">{t('sohozJob.cv.dropHint')}</p>
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            accept=".pdf,.docx,application/pdf,image/*"
                            className="hidden"
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />
                    </div>

                    {/* Selected files */}
                    {files.length > 0 && (
                        <div className="space-y-1.5">
                            {files.map((f, i) => (
                                <FileRow
                                    key={`${f.name}-${f.size}-${i}`}
                                    file={f}
                                    onRemove={() => removeFile(i)}
                                    onPreview={(url, name) => setPreview({ url, name })}
                                    previewLabel={t('sohozJob.cv.viewImage')}
                                />
                            ))}
                        </div>
                    )}

                    {/* Paste-text section */}
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setShowText((v) => !v)}
                            className="w-full flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <IconChevronDown size={15} className={`transition-transform ${showText ? 'rotate-180' : ''}`} />
                            {t('sohozJob.cv.pasteToggle')}
                        </button>
                        {showText && (
                            <div className="px-3 pb-3 pt-1 border-t border-zinc-100 dark:border-zinc-800">
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t('sohozJob.aiPlaceholder')}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                                />
                            </div>
                        )}
                    </div>

                    {noCredit ? (
                        <p className="text-[12px] font-medium text-red-500">{t('sohozJob.cv.noCredit')}</p>
                    ) : isFree ? (
                        /* walletCreditCost_FORM_FILL === 0 → free */
                        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-zinc-700 dark:text-zinc-200">
                            <span>{t('sohozJob.cv.creditLead')}</span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-bold uppercase tracking-wide">
                                <IconGift size={12} /> {t('sohozJob.cv.freeNow')}
                            </span>
                        </p>
                    ) : formFillCost != null ? (
                        /* Paid — show the real per-analysis fee */
                        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-zinc-700 dark:text-zinc-200">
                            <span>{t('sohozJob.cv.creditLead')}</span>
                            <span className="font-bold text-sm text-zinc-900 dark:text-white tabular-nums">{toLocalDigits(formFillCost, isBn)}</span>
                            <span>{t('sohozJob.cv.creditUnit')}</span>
                        </p>
                    ) : null}
                </div>

                <DialogFooter className="mt-5 items-center">
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 dark:text-zinc-500 sm:mr-auto">
                        <IconLock size={13} /> {t('sohozJob.cv.notSaved')}
                    </span>
                    <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-60">
                        {t('common.cancel')}
                    </button>
                    <button type="button" onClick={handleAnalyze} disabled={loading || noCredit} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                        {loading
                            ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {t('sohozJob.analyzing')}</>
                            : <><IconSparkles size={16} /> {t('sohozJob.analyze')}</>}
                    </button>
                </DialogFooter>
            </DialogContent>

            {/* Full-size image preview */}
            <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
                <DialogContent className="max-w-3xl p-2 bg-zinc-900 border-zinc-800">
                    <DialogTitle className="sr-only">{preview?.name || t('sohozJob.cv.viewImage')}</DialogTitle>
                    {preview && (
                        <img src={preview.url} alt={preview.name} className="w-full max-h-[80vh] object-contain rounded-lg" />
                    )}
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
